/**
 * MetadataInput のバリデーション (Zod 使用)。
 * フォーム送信前に呼び出し、エラーマップを返す。
 */

import { z } from 'zod';

import { type MetadataInput, toVehicleMetadata } from './useAppStore';

const MIN_YEAR = 1900;
const MAX_YEAR = 2100;
const MAX_MILEAGE = 999_999;

const MetadataSchema = z.object({
  model: z.string().min(1, '車種は必須'),
  year: z
    .number({ invalid_type_error: '西暦 4 桁の数字を入力' })
    .int('整数を入力')
    .gte(MIN_YEAR, `${MIN_YEAR} 年以降を入力`)
    .lte(MAX_YEAR, `${MAX_YEAR} 年以前を入力`),
  grade: z.string().min(1, 'グレードは必須'),
  mileage: z
    .number({ invalid_type_error: '数字を入力' })
    .int('整数を入力')
    .gte(0, '0 以上を入力')
    .lte(MAX_MILEAGE, `${MAX_MILEAGE.toLocaleString()} km 以下を入力`),
  appeals: z.tuple([
    z.string().min(1, 'アピール1は必須'),
    z.string().min(1, 'アピール2は必須'),
    z.string().min(1, 'アピール3は必須'),
  ]),
});

export type ValidationErrors = Partial<Record<string, string>>;

export interface ValidationResult {
  readonly ok: boolean;
  readonly errors: ValidationErrors;
}

export function validateMetadata(input: MetadataInput): ValidationResult {
  const vehicle = toVehicleMetadata(input);
  const parsed = MetadataSchema.safeParse({
    model: vehicle.model,
    year: Number.parseInt(input.yearInput, 10),
    grade: vehicle.grade,
    mileage: Number.parseInt(input.mileageInput.replace(/[,、\s]/g, ''), 10),
    appeals: vehicle.appeals,
  });

  if (parsed.success) {
    return { ok: true, errors: {} };
  }

  const errors: ValidationErrors = {};
  for (const issue of parsed.error.issues) {
    const keyPath = issue.path;
    if (keyPath.length === 0) continue;
    const [head, ...rest] = keyPath;
    const field = mapFieldName(String(head), rest);
    if (!errors[field]) errors[field] = issue.message;
  }
  return { ok: false, errors };
}

function mapFieldName(head: string, rest: readonly (string | number)[]): string {
  if (head === 'appeals' && rest.length === 1) {
    const idx = Number(rest[0]);
    return `appeal${idx + 1}`;
  }
  return head;
}
